/**
 * Market (country) configuration for the booking flow.
 *
 * The market is the single, data-driven source of truth for everything that
 * differs between countries: which clinics are shown, the display currency,
 * the phone/address country defaults, which package-type pills are offered,
 * the legal consent boxes and the payment logos.
 *
 * Deep-linked via `?country=de` (ISO 3166-1 alpha-2; `?country=ch` works too).
 * The set of available markets is derived from distinct `clinics.country`
 * values; the active market lives in the booking store.
 */

import type { BookingLanguage } from "@/types/booking";

/** ISO 3166-1 alpha-2 country code (lower-case) identifying a market. */
export type MarketCountry = "ch" | "de" | "nl" | "at";

/** Default market — matches the `clinics.country` column default. */
export const DEFAULT_MARKET: MarketCountry = "ch";

// Legal-page URLs per market + language. Market-specific (German also appears in
// the CH market), so they can't come from the language-keyed translations.
// pp = privacy policy, tos = terms & conditions, mds = medical-data-sharing,
// imprint = the Impressum / legal notice.
export type LegalUrls = {
  pp: string;
  tos: string;
  mds: string;
  imprint: string;
};

const legal = (prefix: string): LegalUrls => ({
  imprint: `https://www.aheadhealth.com${prefix}/imprint`,
  pp: `https://www.aheadhealth.com${prefix}/privacy`,
  tos: `https://www.aheadhealth.com${prefix}/terms-conditions`,
  mds: `https://www.aheadhealth.com${prefix}/medical-data-sharing`,
});

const LEGAL_URLS: Record<
  MarketCountry,
  Partial<Record<BookingLanguage, LegalUrls>>
> = {
  ch: {
    "en-US": legal(""),
    "de-DE": legal("/de"),
    "fr-FR": legal("/ch/fr"),
  },
  de: {
    "en-US": legal("/de/de"),
    "de-DE": legal("/de/de"),
  },
  // Unlike DE, NL has English legal pages (/nl/en), verified live 2026-10-21.
  nl: {
    "en-US": legal("/nl/en"),
    "nl-NL": legal("/nl/nl"),
  },
  // SKELETON: mirrors the DE structure. VERIFY the real aheadhealth.com/at
  // legal URLs before AT opens — Austrian consumer law is not German law, so
  // an unreviewed ToS link here is a legal risk, not a copy nit.
  at: {
    "en-US": legal("/at/de"),
    "de-DE": legal("/at/de"),
  },
};

export const getLegalUrls = (
  market: MarketCountry,
  language: BookingLanguage,
): LegalUrls =>
  LEGAL_URLS[market][language] ?? Object.values(LEGAL_URLS[market])[0];

// Marketing-site homepage per market + language. Kept apart from LEGAL_URLS on
// purpose: the legal paths were verified page by page and some are legacy
// aliases (CH German legal lives under /de), while the homepage is the
// canonical /<country>/<language>/ tree.
//
// Probed live on 2026-09-08. A bare https://www.aheadhealth.com/ 307s to
// /ch/de/ for everyone, which is the bug in AHD-8451: a German user clicking
// the logo landed on the Swiss site. /de/en is a 404, so English readers in the
// German market get the German page, same as the legal table. /at/de and
// /at/en were both 404 at probe time: the Austrian site is not published yet,
// and the AT market is still hidden, so this is the canonical shape waiting
// for the page rather than a live link.
const MARKETING_HOME_PATH: Record<
  MarketCountry,
  Partial<Record<BookingLanguage, string>>
> = {
  ch: { "en-US": "/ch/en", "de-DE": "/ch/de", "fr-FR": "/ch/fr" },
  de: { "en-US": "/de/de", "de-DE": "/de/de" },
  nl: { "en-US": "/nl/en", "nl-NL": "/nl/nl" },
  at: { "en-US": "/at/de", "de-DE": "/at/de" },
};

/** The marketing site's locale prefix ("/ch/en") for the market being booked
 *  in, in the visitor's language where that version of the site exists. */
export const getMarketingPrefix = (
  market: MarketCountry,
  language: BookingLanguage,
): string =>
  MARKETING_HOME_PATH[market][language] ??
  Object.values(MARKETING_HOME_PATH[market])[0];

/** Where the Ahead logo takes the user: the marketing homepage for the
 *  market they are booking in, in their language where that page exists. */
export const getHomepageUrl = (
  market: MarketCountry,
  language: BookingLanguage,
): string =>
  `https://www.aheadhealth.com${getMarketingPrefix(market, language)}/`;

// Version ids of the two Swiss documents a booking consents to (AHD-12757).
// Written on every CH booking's consent record, so a dispute can be matched to
// the exact text that was accepted. The general T&Cs page carries
// "Effective Date: 13-03-2025". The Membership T&Cs page carries no date yet;
// 20260427 is the id Laurent set for the current text. Bump a value here the
// day its document changes, and nowhere else.
export const CH_TERMS_VERSION = "20250313";
export const CH_MEMBERSHIP_TERMS_VERSION = "20260427";

/** The Membership T&Cs page. Swiss-only, because membership is; the page lives
 *  under the same /ch/<language>/ tree as the homepage (verified 2026-09-08:
 *  /ch/en, /ch/de and /ch/fr are 200, the bare /membership-terms is a 404). */
export const getMembershipTermsUrl = (language: BookingLanguage): string =>
  `${getHomepageUrl("ch", language)}membership-terms`;

/** Every market the booking flow knows how to localise, in display order. */
export const SUPPORTED_MARKETS: readonly MarketCountry[] = [
  "ch",
  "de",
  "nl",
  "at",
] as const;

/** Narrow an arbitrary string to a supported market, or null if unknown. */
export const parseMarket = (
  value: string | null | undefined,
): MarketCountry | null => {
  if (!value) return null;
  const normalised = value.trim().toLowerCase();
  return (SUPPORTED_MARKETS as readonly string[]).includes(normalised)
    ? (normalised as MarketCountry)
    : null;
};

/** Read the active market from URL search params (`?country=de` / `?country=ch`). */
export const parseMarketFromUrl = (
  params: URLSearchParams,
): MarketCountry | null => parseMarket(params.get("country"));

/** Booking-params fragment to persist the active market in the URL. */
export const marketUrlParams = (
  market: MarketCountry,
): Record<string, string> => ({ country: market });

/** Where a market places its currency symbol relative to the amount. */
export type SymbolPosition = "before" | "after" | "none";

/**
 * How a market renders money — the single source of truth for currency
 * presentation. `formatPrice` and every price component read it, so the
 * "before / after / none" decision lives here, not in scattered `market ===
 * "nl"` or `language.startsWith("nl")` checks.
 *
 * - `code`         ISO code the Stripe prices are actually in (labels real amounts).
 * - `symbol`       standalone symbol ("CHF", "€"); unused when `position` is "none".
 * - `position`     where the symbol sits; "none" = the market shows no symbol (NL).
 * - `groupingLocale` Intl locale for thousands grouping (de-CH apostrophe, de-DE dot).
 * - `swissApostrophe` post-process the grouped string's spaces into apostrophes (CH).
 * - `wholeAmountSuffix` appended to whole amounts (Dutch ",-"), else "". It is
 *                  notation rather than a symbol, so it stays even when the
 *                  symbol is excluded.
 */
export interface CurrencyDisplay {
  code: string;
  symbol: string;
  position: SymbolPosition;
  groupingLocale: string;
  swissApostrophe: boolean;
  wholeAmountSuffix: string;
}

const CURRENCY_DISPLAY: Record<MarketCountry, CurrencyDisplay> = {
  ch: {
    code: "CHF",
    symbol: "CHF",
    position: "before",
    groupingLocale: "de-CH",
    swissApostrophe: true,
    wholeAmountSuffix: "",
  },
  de: {
    code: "EUR",
    symbol: "€",
    position: "after",
    groupingLocale: "de-DE",
    swissApostrophe: false,
    wholeAmountSuffix: "",
  },
  // Dutch retail convention: no symbol at all, just the amount with a ",-"
  // whole-euro suffix (1.889,-, 299,-).
  nl: {
    code: "EUR",
    symbol: "€",
    position: "none",
    groupingLocale: "de-DE",
    swissApostrophe: false,
    wholeAmountSuffix: ",-",
  },
  // Austrian convention is the German one: "1.889 €", decimal comma, symbol
  // after. de-AT groups identically to de-DE; named explicitly so a future
  // locale-specific difference lands here rather than silently in de-DE.
  at: {
    code: "EUR",
    symbol: "€",
    position: "after",
    groupingLocale: "de-AT",
    swissApostrophe: false,
    wholeAmountSuffix: "",
  },
};

/** How the market renders money — pass to `formatPrice` / `<CurrencyAmount>`. */
export const getCurrencyDisplay = (market: MarketCountry): CurrencyDisplay =>
  CURRENCY_DISPLAY[market];

/** Currency ISO code shown for a market (EUR for DE/NL, CHF for CH). */
export const getCurrencyForMarket = (market: MarketCountry): string =>
  CURRENCY_DISPLAY[market].code;

/** Standalone currency symbol for labels rendered outside `formatPrice`. */
export const getCurrencySymbolForMarket = (market: MarketCountry): string =>
  CURRENCY_DISPLAY[market].symbol;

/** Default phone / address country (ISO alpha-2, upper-case) for a market. */
export const getDefaultCountryCode = (market: MarketCountry): string =>
  market === "de"
    ? "DE"
    : market === "nl"
      ? "NL"
      : market === "at"
        ? "AT"
        : "CH";

/**
 * Per-market feature capabilities — the single source of truth for what a
 * market offers. Replaces scattered `market === "ch"` / `isGermany` checks:
 * add a market here and every capability follows.
 *
 * - `imaging`      full-body MRI check-ups and scan-based packages/copy.
 * - `membership`   the Ahead membership flow, upsells and member pricing (still
 *                  gated behind the `membership_v2` feature flag on top of this).
 * - `advisoryBoard` the "experts behind your health journey" section below the
 *                  booking flow.
 * - `advisoryCall` the bookable advisory call offered on the advisory board.
 * - `prepay`       the booking is PAID UP FRONT as a one-off Stripe charge
 *                  instead of being submitted as a request. In practice the
 *                  inverse of `membership`: CH sells the yearly membership
 *                  subscription, DE/NL sell the single appointment outright.
 * - `supportPhone` the support line shown in the header and contact modal (also
 *                  the `tel:` target), or null where support is WhatsApp-only.
 * - `supportWhatsApp` digits of the WhatsApp support number, for wa.me links.
 *
 * The blood-only launch markets (DE, NL) offer no imaging and no membership;
 * they prepay a one-off EUR invoice instead. NL runs async-first support on
 * WhatsApp only, with no phone line (AHD-8731).
 */
interface MarketFeatures {
  imaging: boolean;
  membership: boolean;
  advisoryBoard: boolean;
  advisoryCall: boolean;
  prepay: boolean;
  supportPhone: string | null;
  supportWhatsApp: string;
}

const SWISS_SUPPORT = {
  supportPhone: "+41 44 797 69 46",
  supportWhatsApp: "41798078664",
};

const MARKET_FEATURES: Record<MarketCountry, MarketFeatures> = {
  ch: {
    imaging: true,
    membership: true,
    advisoryBoard: true,
    advisoryCall: true,
    prepay: false,
    ...SWISS_SUPPORT,
  },
  de: {
    imaging: false,
    membership: false,
    advisoryBoard: true,
    advisoryCall: true,
    prepay: true,
    ...SWISS_SUPPORT,
  },
  nl: {
    imaging: false,
    membership: false,
    advisoryBoard: false,
    advisoryCall: false,
    prepay: true,
    // WhatsApp only: no phone line is shown in NL (header icon, no call option).
    supportPhone: null,
    supportWhatsApp: "31612640438",
  },
  // Austria (IHR LABOR, Vienna) — blood draw only, so no imaging and no
  // membership: the membership renews an annual blood test bundled with a
  // scan tier we do not sell here. Prepaid like DE/NL: a single appointment
  // charged up front instead of a subscription.
  // SKELETON: no Austrian support number yet, so the Swiss line answers (the
  // same choice DE made). Swap in a +43 number when one exists.
  at: {
    imaging: false,
    membership: false,
    advisoryBoard: true,
    advisoryCall: false,
    prepay: true,
    ...SWISS_SUPPORT,
  },
};

/** True when the market offers full-body MRI / scan-based packages. */
export const marketHasImaging = (market: MarketCountry): boolean =>
  MARKET_FEATURES[market].imaging;

/** True when the market offers membership (before the membership_v2 flag). */
export const marketHasMembership = (market: MarketCountry): boolean =>
  MARKET_FEATURES[market].membership;

/** True when the market shows the advisory board (experts) section. */
export const marketHasAdvisoryBoard = (market: MarketCountry): boolean =>
  MARKET_FEATURES[market].advisoryBoard;

/** True when the market offers a bookable advisory call. */
export const marketHasAdvisoryCall = (market: MarketCountry): boolean =>
  MARKET_FEATURES[market].advisoryCall;

/**
 * True when the booking is paid up front (one-off Stripe invoice) rather than
 * submitted as a request. Drives the details-step CTA and the one-off branch of
 * the embedded payment modal.
 */
export const marketPrepays = (market: MarketCountry): boolean =>
  MARKET_FEATURES[market].prepay;

/**
 * Payment methods offered on the one-off invoice, per market.
 *
 * EXPLICIT on purpose. Leaving `payment_settings.payment_method_types` unset
 * (Stripe's dynamic payment methods) is the tidier integration, but it renders
 * an EMPTY Payment Element unless dynamic payment methods are configured for
 * that currency on the account — which is what happened in CI: the modal opened,
 * the Stripe iframe mounted, and no card field ever appeared. The CH membership
 * flow has always pinned an explicit list and has always worked, so the one-off
 * path does the same.
 *
 * This list MUST equal `payment_settings.payment_method_types` on the invoice in
 * the create-booking-payment edge function. Edge functions can't import from
 * `src/`, so the two are duplicated deliberately; a mismatch fails at confirm
 * with "collected using automatic payment methods and cannot be confirmed
 * through the API configured with payment_method_types".
 *
 * Every method here must be ENABLED on both Stripe accounts (live + sandbox) or
 * invoice creation errors naming the offending one.
 *
 * `"ideal"` (Wero — "iDEAL | Wero" in the dashboard) works only because the
 * invoice uses `collection_method: "send_invoice"`. On `charge_automatically`
 * Stripe rejects it outright, verbatim:
 *
 *   "The payment method type `ideal` cannot be used with invoices that have
 *    the `collection_method` set to `charge_automatically`."
 *
 * That is a structural rule about redirect-based single-use methods, NOT an
 * account capability. If anyone moves the edge function back to
 * charge_automatically, iDEAL must come out of the NL list in the same commit
 * or every NL checkout 500s.
 */
const PREPAY_PAYMENT_METHODS: Record<MarketCountry, readonly string[]> = {
  ch: [], // CH doesn't prepay — the membership subscription owns its own list.
  de: ["card", "klarna", "paypal"],
  nl: ["card", "ideal", "klarna", "paypal"],
  // EPS is the Austrian bank-transfer method, the local equivalent of NL's
  // iDEAL, and like iDEAL it is single-use — it only works because the invoice
  // is collection_method: "send_invoice". It must be ACTIVATED in both the
  // live and the sandbox Stripe account before AT opens, or the Payment
  // Element silently drops it.
  at: ["card", "eps", "klarna", "paypal"],
};

export const getPrepayPaymentMethods = (
  market: MarketCountry,
): readonly string[] => PREPAY_PAYMENT_METHODS[market];

/** Support line for a market, or null where support is WhatsApp-only. */
export const getSupportPhone = (market: MarketCountry): string | null =>
  MARKET_FEATURES[market].supportPhone;

/** WhatsApp support link for a market. */
export const getWhatsAppSupportUrl = (market: MarketCountry): string =>
  `https://wa.me/${MARKET_FEATURES[market].supportWhatsApp}`;

/**
 * Package types force-shown via `?force=mri` (comma-separated, e.g.
 * `?force=mri,membership`). Lets an MRI landing page deep-link into a market
 * that hides MRI by default and still land on the MRI pill (AHD-7129), instead
 * of being silently routed to the blood test.
 */
export const parseForcedPackageTypes = (
  params: URLSearchParams,
): readonly string[] => {
  const raw = params.get("force");
  if (!raw) return [];
  return raw
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
};

/**
 * True when a package type is unavailable in the given market — derived from
 * the market's feature capabilities: the `mri` pill needs `imaging`, the
 * `membership` pill needs `membership`. A type listed in `forced` (from
 * `?force=…`) is always treated as visible, overriding this.
 */
export const isPackageTypeHiddenInMarket = (
  market: MarketCountry,
  packageType: string,
  forced: readonly string[] = [],
): boolean => {
  if (forced.includes(packageType)) return false;
  if (packageType === "mri") return !marketHasImaging(market);
  if (packageType === "membership") return !marketHasMembership(market);
  return false;
};

/** True when the market requires the German private-treatment consent boxes. */
export const marketRequiresGermanConsent = (market: MarketCountry): boolean =>
  market === "de";

/**
 * Markets that show the AUSTRIAN consent set: the lab/partner-doctor contract,
 * the release from medical confidentiality (§ 54 ÄrzteG 1998) and the express
 * request to start before the withdrawal period expires.
 *
 * Structurally the same idea as marketRequiresGermanConsent: verbatim national
 * legal text, required, shown only in the market it was written for. Unlike the
 * German set it is NOT pinned to one language: Austria sells in German and
 * English, so the text follows the interface language and both are maintained.
 */
export const marketRequiresAustrianConsent = (market: MarketCountry): boolean =>
  market === "at";

/**
 * Markets that show the SWISS consent pair: ToS-only (no Privacy-Policy
 * read-confirmation, per Kellerhals Carrard — the PP is not a contract, so
 * asking to confirm it could disadvantage us later) plus the CH
 * medical-data-sharing wording. DE and NL have their own legal advice and
 * keep the `tosAndPp.*` / `medicalData.*` copy instead.
 *
 * AT rides the Swiss pair FOR NOW, by decision — Austria has had no separate
 * legal review yet. It is the conservative of the two we have (it asks for
 * less), but it is still an interim choice: revisit before AT opens, together
 * with LEGAL_URLS.at, which is what these consent sentences link to.
 */
export const marketUsesSwissConsent = (market: MarketCountry): boolean =>
  market === "ch" || market === "at";

/** TWINT is a Swiss-only payment method; hide its logo outside Switzerland. */
export const marketShowsTwint = (market: MarketCountry): boolean =>
  market === "ch";

/**
 * Languages offered in a market, in display order. French is Switzerland-only —
 * the German market (`?country=de`) offers English and German only.
 */
export const getLanguagesForMarket = (
  market: MarketCountry,
): BookingLanguage[] => {
  if (market === "de" || market === "at") return ["en-US", "de-DE"];
  if (market === "nl") return ["en-US", "nl-NL"];
  return ["en-US", "de-DE", "fr-FR"];
};

/** Default language for a market: the national language for DE/NL, English otherwise. */
export const getDefaultLanguageForMarket = (
  market: MarketCountry,
): BookingLanguage =>
  market === "de" || market === "at"
    ? "de-DE"
    : market === "nl"
      ? "nl-NL"
      : "en-US";

/**
 * Force a language into one the market actually offers. A French selection (URL
 * `locale=fr`, a persisted choice, or a CH→DE market switch) collapses to the
 * market default in the German market, where French isn't available.
 */
export const coerceLanguageForMarket = (
  market: MarketCountry,
  language: BookingLanguage,
): BookingLanguage =>
  getLanguagesForMarket(market).includes(language)
    ? language
    : getDefaultLanguageForMarket(market);
